import { ApolloClient } from '@apollo/client';
import { CAMPAIGN_LIST_ITEM_QUERY } from './campaigns.gql';

export interface CampaignListRecord {
	campaignId: string;
	name: string;
	description?: string | null;
	startDate: string;
	endDate: string;
	isActive: boolean;
}

interface CampaignListItemResponse {
	campaign?: CampaignListRecord | null;
}

export interface SelectCampaignAfterSaveOptions {
	campaignId: string;
	client: ApolloClient<any>;
	currentCampaigns: CampaignListRecord[];
	onDataUpdate: (updatedCampaigns: CampaignListRecord[]) => void;
	onCampaignSelect: (campaign: CampaignListRecord) => void;
}

/**
 * Fetches the latest campaign data from the server and updates the list,
 * then selects and highlights the campaign in the grid and details panel.
 * 
 * @param options Configuration options for selecting the campaign
 * @returns Promise that resolves when the operation is complete
 */
export async function selectCampaignAfterSave(
	options: SelectCampaignAfterSaveOptions
): Promise<void> {
	const { campaignId, client, currentCampaigns, onDataUpdate, onCampaignSelect } = options;

	try {
		// Step 1: Fetch the latest campaign data from the server
		const { data } = await client.query<CampaignListItemResponse>({
			query: CAMPAIGN_LIST_ITEM_QUERY,
			variables: { id: campaignId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedCampaign = data?.campaign;

		if (!fetchedCampaign) {
			console.warn(`Campaign with ID ${campaignId} not found after save`);
			return;
		}

		// Step 2: Check if campaign exists in current list
		const existingCampaignIndex = currentCampaigns.findIndex(
			c => c.campaignId === campaignId
		);

		let updatedCampaigns: CampaignListRecord[];

		if (existingCampaignIndex >= 0) {
			// Campaign exists - replace with updated data
			updatedCampaigns = [...currentCampaigns];
			updatedCampaigns[existingCampaignIndex] = fetchedCampaign;
		} else {
			// New campaign - append to the list
			updatedCampaigns = [...currentCampaigns, fetchedCampaign];
		}

		// Step 3: Update the list data (with sorting to maintain consistency)
		const sortedCampaigns = [...updatedCampaigns].sort((a, b) => a.name.localeCompare(b.name));
		onDataUpdate(sortedCampaigns);

		// Step 4: Select the campaign (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onCampaignSelect(fetchedCampaign);
		}, 50);
	} catch (error) {
		console.error('Error selecting campaign after save:', error);
		throw error;
	}
}